import React, { useEffect, useMemo, useState } from 'react';

import { useProcurement } from '../context/ProcurementContext';

import ApprovalsDetailModal from './ApprovalsDetailModal';
import { APPROVAL_STATUS, RECORD_TYPES, normalisePoRow, normalisePrRow, sortPendingFirst } from './approvalsHelpers';
import ApprovalsRegisterTable from './ApprovalsRegisterTable';
import ApprovalsSummaryCards from './ApprovalsSummaryCards';
import SegmentedControl from './SegmentedControl';

import { apiClient } from 'Helpers/network';

import { useCustomEvent } from 'Hooks/useGlobalDOMEvents';

import SearchIcon from 'Assets/icons/search.svg';

const TYPE_FILTER = { ALL: 'all', PR: RECORD_TYPES.PR, EA: RECORD_TYPES.EA, DC: RECORD_TYPES.DC };
const STATUS_FILTER = { ALL: 'all', ...APPROVAL_STATUS };

const DEFAULT_FILTERS = { type: TYPE_FILTER.ALL, status: STATUS_FILTER.ALL };

/** Max rows a single feed request may return (matches the endpoints' per_page validation). */
const FEED_PER_PAGE = 100;

/** How many merged rows to reveal per "Load more" step. */
const PAGE_SIZE = 25;

const TYPE_OPTIONS = [
    { value: TYPE_FILTER.ALL, label: $t('All') },
    { value: TYPE_FILTER.PR, label: $t('PRs') },
    { value: TYPE_FILTER.EA, label: $t('EAs') },
    { value: TYPE_FILTER.DC, label: $t('DCs') },
];

const STATUS_OPTIONS = [
    { value: STATUS_FILTER.ALL, label: $t('All') },
    { value: STATUS_FILTER.PENDING, label: $t('Pending') },
    { value: STATUS_FILTER.APPROVED, label: $t('Approved') },
    { value: STATUS_FILTER.REJECTED, label: $t('Rejected') },
];

/** Read the persisted filters for this user+event, guarding a malformed / unknown-enum payload. */
const loadFilters = (storageKey) => {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return DEFAULT_FILTERS;
        }

        const parsed = JSON.parse(raw);
        const type = Object.values(TYPE_FILTER).includes(parsed.type) ? parsed.type : TYPE_FILTER.ALL;
        const status = Object.values(STATUS_FILTER).includes(parsed.status) ? parsed.status : STATUS_FILTER.ALL;

        return { type, status };
    } catch (e) {
        return DEFAULT_FILTERS;
    }
};

/**
 * IB-701 — the Approvals tab. Merges the existing PR + PO status-scoped feeds into one register
 * (tagging each row PR / EA / DC client-side), fronted by the approvals-summary aggregate cards.
 * TYPE + status filters are persisted in localStorage per user+event; pending rows sort first.
 * There is no Approve action here — rows link out to the existing detail screens.
 */
const ApprovalsTab = () => {
    const { settings, routes } = useProcurement();
    const currencySymbol = settings.currencySymbol;

    const storageKey = useMemo(
        () => `procurement:approvals:filters:${settings.eventSlug ?? 'event'}:${settings.authUserId ?? 'user'}`,
        [settings.eventSlug, settings.authUserId],
    );

    const [filters, setFilters] = useState(() => loadFilters(storageKey));
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [detailRow, setDetailRow] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    const openDetail = (row) => {
        setDetailRow(row);
        setDetailOpen(true);
    };

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(filters));
        } catch (e) {
            // Persistence is best-effort; ignore quota / privacy-mode failures.
        }
    }, [storageKey, filters]);

    // Debounce the search box (~300ms) so the register filters live as the user types.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);

        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchSummary = () => {
        if (!routes.approvalsSummaryUrl) {
            return;
        }

        setSummaryLoading(true);
        apiClient
            .get(routes.approvalsSummaryUrl)
            .then(({ data }) => setSummary(data))
            .finally(() => setSummaryLoading(false));
    };

    // Each feed is paginated (per_page caps at 100), so walk every page and merge — the register
    // must never silently truncate a bucket with >100 rows. Page 1 reveals last_page, then the
    // remaining pages are fetched in parallel.
    const fetchAllPages = (url) => {
        if (!url) {
            return Promise.resolve([]);
        }

        return apiClient.get(url, { params: { per_page: FEED_PER_PAGE, page: 1 } }).then((first) => {
            const firstRows = first.data?.data ?? [];
            const lastPage = first.data?.meta?.last_page ?? 1;

            if (lastPage <= 1) {
                return firstRows;
            }

            const rest = Array.from({ length: lastPage - 1 }, (_, index) => apiClient.get(url, { params: { per_page: FEED_PER_PAGE, page: index + 2 } }));

            return Promise.all(rest).then((responses) => responses.reduce((acc, res) => acc.concat(res.data?.data ?? []), firstRows));
        });
    };

    const fetchRegister = () => {
        setRowsLoading(true);

        const prUrls = [routes.fetchOpenedPurchaseRequisitions, routes.fetchApprovedPurchaseRequisitions, routes.fetchRejectedPurchaseRequisitions];
        const poUrls = [routes.fetchPurchaseOrderUrl, routes.fetchApprovedPurchaseOrdersUrl, routes.fetchRejectedPurchaseOrdersUrl];

        Promise.all([Promise.all(prUrls.map(fetchAllPages)), Promise.all(poUrls.map(fetchAllPages))])
            .then(([prFeeds, poFeeds]) => {
                const prRows = prFeeds.flat().map(normalisePrRow);
                const poRows = poFeeds.flat().map(normalisePoRow);

                setRows([...prRows, ...poRows]);
            })
            .finally(() => setRowsLoading(false));
    };

    useCustomEvent({
        eventName: 'REFETCH_PROCUREMENT_DASHBOARD',
        onSignal: () => {
            fetchSummary();
            fetchRegister();
        },
    });

    useEffect(() => {
        fetchSummary();
        fetchRegister();
    }, []);

    const filteredRows = useMemo(() => {
        const filtered = rows.filter((row) => {
            if (filters.type !== TYPE_FILTER.ALL && row.type !== filters.type) {
                return false;
            }

            if (filters.status !== STATUS_FILTER.ALL && row.status !== filters.status) {
                return false;
            }

            if (search) {
                const haystack = `${row.reference} ${row.procurement} ${row.department ?? ''} ${row.requestor ?? ''}`.toLowerCase();
                if (!haystack.includes(search)) {
                    return false;
                }
            }

            return true;
        });

        return sortPendingFirst(filtered);
    }, [rows, filters, search]);

    // Reset the reveal window whenever the filtered set changes so "Load more" always starts fresh.
    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [filters, search, rows]);

    const visibleRows = useMemo(() => filteredRows.slice(0, visibleCount), [filteredRows, visibleCount]);
    const hasMore = filteredRows.length > visibleRows.length;

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            <ApprovalsSummaryCards summary={summary} currencySymbol={currencySymbol} loading={summaryLoading} />

            <div className="tw-flex tw-flex-col tw-gap-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        <SegmentedControl
                            label={$t('Type')}
                            value={filters.type}
                            onChange={(type) => setFilters((prev) => ({ ...prev, type }))}
                            options={TYPE_OPTIONS}
                        />
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                        <div className="tw-relative tw-w-64">
                            <SearchIcon className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-my-auto tw-ms-3 tw-h-3.5 tw-w-3.5 tw-text-charcoal-50" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={$t('Search approvals…')}
                                className="tw-w-full tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-py-2 tw-ps-9 tw-pe-3 tw-text-sm tw-text-charcoal placeholder:tw-text-charcoal-50 focus:tw-border-blue focus:tw-outline-none"
                            />
                        </div>
                        <SegmentedControl
                            label={$t('Status')}
                            value={filters.status}
                            onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
                            options={STATUS_OPTIONS}
                        />
                    </div>
                </div>

                <ApprovalsRegisterTable
                    rows={visibleRows}
                    currencySymbol={currencySymbol}
                    loading={rowsLoading}
                    emptyText={$t('No approval records match the current filters.')}
                    onView={openDetail}
                />

                {!rowsLoading && filteredRows.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <div className="tw-text-xs tw-text-charcoal-50">
                            {$t('Showing {{shown}} of {{total}}', { shown: visibleRows.length, total: filteredRows.length })}
                        </div>
                        {hasMore && (
                            <button
                                type="button"
                                onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                                className="tw-inline-flex tw-items-center tw-rounded-md tw-border tw-border-blue-25 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-blue tw-transition-colors hover:tw-bg-blue-10"
                            >
                                {$t('Load more')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <ApprovalsDetailModal row={detailRow} isOpen={detailOpen} onClose={() => setDetailOpen(false)} currencySymbol={currencySymbol} />
        </div>
    );
};

export default ApprovalsTab;
